import React, { useId } from "react";
import { FilterBar, TabBar } from "@/components/ui";
import shared from "@/components/utils/shared.module.css";
import { StrategyFilter } from "../types";
import { ALL_ORDERS_CATEGORY, CATEGORY_OPTIONS, defaultStartDate, type OrderTypeFilter, type PriorityFilter, type DecisionFilter, type LastOrderFilter, type MarketableFilter, type RowTypeFilter, type FilterLevel } from "../utils";
import type { CountryTab } from "../hooks/useOrderRecommendations";
import styles from "../OrderRecommendationList.module.css";

const COUNTRY_TABS = [
  { value: "domestic", label: "Domestic" },
  { value: "global", label: "Global" },
];

interface RecommendationFiltersProps {
  countryTab: CountryTab;
  setCountryTab: (tab: CountryTab) => void;
  recStartDate: string;
  setRecStartDate: (date: string) => void;
  strategyFilter: StrategyFilter;
  setStrategyFilter: (filter: StrategyFilter) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedCategories: string[];
  toggleCategory: (cat: string) => void;
  setSelectedCategories: (cats: string[]) => void;
  /** The active country tab's default chips — Global includes W1/W2/W3, Domestic does not. */
  defaultCategories: string[];
  orderTypeFilter: OrderTypeFilter;
  setOrderTypeFilter: (v: OrderTypeFilter) => void;
  priorityFilter: PriorityFilter;
  setPriorityFilter: (v: PriorityFilter) => void;
  decisionFilter: DecisionFilter;
  setDecisionFilter: (v: DecisionFilter) => void;
  lastOrderFilter: LastOrderFilter;
  setLastOrderFilter: (v: LastOrderFilter) => void;
  marketableFilter: MarketableFilter;
  setMarketableFilter: (v: MarketableFilter) => void;
  rowTypeFilter: RowTypeFilter;
  setRowTypeFilter: (v: RowTypeFilter) => void;
  filterLevel: FilterLevel;
  setFilterLevel: (v: FilterLevel) => void;
}

/**
 * Search, the Domestic / Global switch and the Filters panel. Every filter
 * lives behind the one Filters button, as on every other page; search keeps
 * its own clear control and is not counted as a filter.
 */
export const RecommendationFilters: React.FC<RecommendationFiltersProps> = ({
  countryTab, setCountryTab,
  recStartDate, setRecStartDate,
  strategyFilter, setStrategyFilter,
  searchTerm, setSearchTerm,
  selectedCategories, toggleCategory, setSelectedCategories, defaultCategories,
  orderTypeFilter, setOrderTypeFilter,
  priorityFilter, setPriorityFilter,
  decisionFilter, setDecisionFilter,
  lastOrderFilter, setLastOrderFilter,
  marketableFilter, setMarketableFilter,
  rowTypeFilter, setRowTypeFilter,
  filterLevel, setFilterLevel,
}) => {
  const categoryLabelId = useId();
  const isDefaultCat = selectedCategories.length === defaultCategories.length &&
    defaultCategories.every(c => selectedCategories.includes(c));

  // Count filters that deviate from their defaults; Clear all resets exactly these.
  const defaultStart = defaultStartDate();
  const activeFilterCount = [
    recStartDate !== defaultStart,
    strategyFilter !== "all",
    !isDefaultCat,
    orderTypeFilter !== "all",
    priorityFilter !== "all",
    decisionFilter !== "all",
    lastOrderFilter !== "all",
    marketableFilter !== "all",
    rowTypeFilter !== "all",
    filterLevel !== "company",
  ].filter(Boolean).length;

  const resetFilters = () => {
    setRecStartDate(defaultStart);
    setStrategyFilter("all");
    setSelectedCategories(defaultCategories);
    setOrderTypeFilter("all");
    setPriorityFilter("all");
    setDecisionFilter("all");
    setLastOrderFilter("all");
    setMarketableFilter("all");
    setRowTypeFilter("all");
    setFilterLevel("company");
  };

  // Order-level filters can never match recommendation rows, so they are
  // disabled (and reset, to avoid a blank table) while viewing recs only.
  const recsOnly = rowTypeFilter === "recommendations";

  const handleRowTypeChange = (v: RowTypeFilter) => {
    setRowTypeFilter(v);
    if (v === "recommendations") {
      setOrderTypeFilter("all");
      setPriorityFilter("all");
      setDecisionFilter("all");
      setLastOrderFilter("all");
      setMarketableFilter("all");
    }
  };

  return (
    <FilterBar
      searchValue={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder="Search company…"
      activeFilterCount={activeFilterCount}
      onClearFilters={resetFilters}
      filters={
        <>
          <div className={shared["filter-group"]}>
            <label htmlFor="orf-rows">Show</label>
            <select id="orf-rows" className={shared.input} value={rowTypeFilter}
              onChange={e => handleRowTypeChange(e.target.value as RowTypeFilter)}>
              <option value="all">Orders + Recs</option>
              <option value="orders">Orders Only</option>
              <option value="recommendations">Recommendations Only</option>
            </select>
          </div>

          <div className={shared["filter-group"]}>
            <label htmlFor="orf-level">Filter level</label>
            <select id="orf-level" className={shared.input} value={filterLevel}
              onChange={e => setFilterLevel(e.target.value as FilterLevel)}>
              <option value="company">Company Level</option>
              <option value="strategy">Strategy Level</option>
            </select>
          </div>

          <div className={shared["filter-group"]}>
            <label htmlFor="orf-start">Recommendations from</label>
            <input id="orf-start" type="date" className={shared.input} value={recStartDate}
              onChange={e => setRecStartDate(e.target.value)} />
          </div>

          <div className={shared["filter-group"]}>
            <label htmlFor="orf-strategy">Strategy</label>
            <select id="orf-strategy" className={shared.input} value={strategyFilter}
              onChange={e => setStrategyFilter(e.target.value as StrategyFilter)}>
              <option value="all">All Strategies</option>
              <option value="lhf">LHF</option>
              <option value="non-lhf">CORE</option>
            </select>
          </div>

          <div className={shared["filter-group"]}>
            <label htmlFor="orf-type">Order type</label>
            <select id="orf-type" className={shared.input} value={orderTypeFilter} disabled={recsOnly}
              onChange={e => setOrderTypeFilter(e.target.value as OrderTypeFilter)}>
              <option value="all">All Types</option>
              <option value="buy">Buy</option>
              <option value="sell">Sell</option>
            </select>
          </div>

          <div className={shared["filter-group"]}>
            <label htmlFor="orf-priority">Priority</label>
            <select id="orf-priority" className={shared.input} value={priorityFilter} disabled={recsOnly}
              onChange={e => setPriorityFilter(e.target.value as PriorityFilter)}>
              <option value="all">All Priority</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          <div className={shared["filter-group"]}>
            <label htmlFor="orf-decision">CIO decision</label>
            <select id="orf-decision" className={shared.input} value={decisionFilter} disabled={recsOnly}
              onChange={e => setDecisionFilter(e.target.value as DecisionFilter)}>
              <option value="all">All Decisions</option>
              <option value="none">Empty</option>
              <option value="continue">Continue</option>
            </select>
          </div>

          <div className={shared["filter-group"]}>
            <label htmlFor="orf-last">Last order</label>
            <select id="orf-last" className={shared.input} value={lastOrderFilter} disabled={recsOnly}
              onChange={e => setLastOrderFilter(e.target.value as LastOrderFilter)}>
              <option value="all">All Last Orders</option>
              <option value="has_pending_codes">Left Orders</option>
              <option value="none">No Last Order</option>
              <option value="has_warning">Has Warning</option>
            </select>
          </div>

          <div className={shared["filter-group"]}>
            <label htmlFor="orf-marketable">Marketable</label>
            <select id="orf-marketable" className={shared.input} value={marketableFilter} disabled={recsOnly}
              onChange={e => setMarketableFilter(e.target.value as MarketableFilter)}>
              <option value="all">All Orders</option>
              <option value="marketable">Marketable Only</option>
            </select>
          </div>

          <div className={`${shared["filter-group"]} ${styles["filter-span"]}`}>
            <span id={categoryLabelId} className={shared["group-label"]}>Category</span>
            <div className={styles["cat-chips"]} role="group" aria-labelledby={categoryLabelId}>
              {CATEGORY_OPTIONS.map(cat => (
                <button
                  key={cat}
                  type="button"
                  aria-pressed={selectedCategories.includes(cat)}
                  title={
                    cat === ALL_ORDERS_CATEGORY
                      ? "Show every pending order regardless of company category. Recommendations still follow the category chips."
                      : undefined
                  }
                  className={[
                    styles["cat-chip"],
                    selectedCategories.includes(cat) ? styles["cat-active"] : "",
                    cat === ALL_ORDERS_CATEGORY ? styles["cat-chip-all"] : "",
                  ].filter(Boolean).join(" ")}
                  onClick={() => toggleCategory(cat)}
                >
                  {cat}
                </button>
              ))}
              <button type="button" className={styles["cat-link"]} onClick={() => setSelectedCategories([])}>None</button>
              <button type="button" className={styles["cat-link"]} onClick={() => setSelectedCategories(defaultCategories)}>Default</button>
            </div>
          </div>
        </>
      }
    >
      <TabBar
        tabs={COUNTRY_TABS}
        value={countryTab}
        onChange={v => setCountryTab(v as CountryTab)}
        ariaLabel="Domestic or Global"
      />
    </FilterBar>
  );
};
